import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { ASSET_TEXT_LIMITS, WORK_ORDER_KIND_LABELS } from '@wodi/domain';
import type { WorkOrderView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Section,
  Select,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  assetKeys,
  assetOptionsQuery,
  partOptionsQuery,
  workOrderQuery,
  workOrderSteps,
} from '../api/assets.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { dateTimeText, dayText, errorText, LoadFailed, NotAllowed } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { AssetStatusTag, WorkOrderStatusTag } from './words.tsx';

// One work order (docs/plans/phase3-operations-modules.md §3.3): its steps and the parts
// used. A part comes out of the chosen store through the stock ledger, earliest expiry first
// unless a batch is chosen with a reason.

const route = getRouteApi('/_app/work-orders/$workOrderId');
const FEFO = 'fefo';

function useStep(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (run: () => Promise<WorkOrderView>) => run(),
    onSuccess: (view) => {
      queryClient.setQueryData(assetKeys.workOrder(id), view);
      // Everything else about assets may have changed; this work order is already fresh.
      void queryClient.invalidateQueries({
        queryKey: assetKeys.all,
        predicate: (q) => q.queryKey[1] !== 'work-order',
      });
    },
  });
}

function AddPart({ order, online }: { order: WorkOrderView; online: boolean }) {
  const options = useQuery(assetOptionsQuery);
  const [store, setStore] = useState<string | null>(null);
  const parts = useQuery({ ...partOptionsQuery(store ?? ''), enabled: store !== null });
  const [item, setItem] = useState<string | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [batch, setBatch] = useState(FEFO);
  const [reason, setReason] = useState('');
  const step = useStep(order.id);
  const chosen = parts.data?.items.find((i) => i.itemId === item);
  const qty = Number(quantity.trim());
  const qtyOk = Number.isInteger(qty) && qty >= 1;
  const ready =
    store !== null && item !== null && qtyOk && (batch === FEFO || reason.trim().length >= 5);
  return (
    <form
      className={styles.form}
      aria-label="Add a part"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        step.mutate(
          () =>
            workOrderSteps.addPart(order.id, {
              storeId: store,
              itemId: item,
              quantity: qty,
              batchId: batch === FEFO ? null : batch,
              batchOverrideReason: batch === FEFO ? null : reason.trim(),
            }),
          {
            onSuccess: () => {
              setItem(null);
              setQuantity('1');
              setBatch(FEFO);
              setReason('');
              toasts.show({ title: 'Part recorded' });
            },
          },
        );
      }}
    >
      <div className={styles.fields}>
        <Select
          label="Store"
          placeholder="Choose the store"
          options={(options.data?.stores ?? []).map((s) => ({ id: s.id, label: s.name }))}
          value={store}
          onChange={(key) => {
            setStore(key === null ? null : String(key));
            setItem(null);
            setBatch(FEFO);
          }}
        />
        <Select
          label="Item"
          placeholder={
            store === null
              ? 'Choose a store first'
              : parts.isPending
                ? 'Loading…'
                : 'Choose the item'
          }
          isDisabled={store === null}
          options={(parts.data?.items ?? []).map((i) => ({
            id: i.itemId,
            label: `${i.name}, ${String(i.available)} ${i.unit} in stock`,
          }))}
          value={item}
          onChange={(key) => {
            setItem(key === null ? null : String(key));
            setBatch(FEFO);
          }}
        />
        <TextField
          label="Quantity"
          {...(chosen === undefined ? {} : { description: `In ${chosen.unit}.` })}
          value={quantity}
          onChange={setQuantity}
          inputMode="numeric"
          width="sm"
          {...(qtyOk ? {} : { errorMessage: 'A whole number, 1 or more.' })}
        />
        <Select
          label="Batch"
          isDisabled={chosen === undefined}
          options={[
            { id: FEFO, label: 'Earliest expiry first' },
            ...(chosen?.lots ?? []).map((l) => ({
              id: l.batchId,
              label: `${l.batchNumber}, expires ${dayText(l.expiryDate)}, ${String(l.quantity)} left`,
            })),
          ]}
          value={batch}
          onChange={(key) => {
            setBatch(key === null ? FEFO : String(key));
          }}
        />
      </div>
      {batch === FEFO ? null : (
        <TextField
          label="Why this batch"
          description="Needed when it isn't the earliest to expire."
          value={reason}
          onChange={setReason}
          maxLength={500}
        />
      )}
      {parts.isError ? (
        <Notice tone="error">The store&apos;s stock couldn&apos;t be loaded. Try again.</Notice>
      ) : null}
      {step.isError ? (
        <Notice tone="error">
          {errorText(step.error, "The part wasn't recorded. Try again.")}
        </Notice>
      ) : null}
      <div className={styles.actions}>
        <Button
          type="submit"
          isDisabled={!online || !ready}
          isPending={step.isPending}
          pendingLabel="Recording…"
        >
          Record part
        </Button>
      </div>
    </form>
  );
}

function CompleteDialog({ order, onClose }: { order: WorkOrderView; onClose: () => void }) {
  const [workDone, setWorkDone] = useState('');
  const [back, setBack] = useState(true);
  const [tried, setTried] = useState(false);
  const step = useStep(order.id);
  return (
    <Dialog
      title="Complete the work order"
      isOpen
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <div className={styles.form}>
        <TextArea
          label="Work done"
          value={workDone}
          onChange={setWorkDone}
          maxLength={ASSET_TEXT_LIMITS.workDone}
          {...(tried && workDone.trim() === '' ? { errorMessage: 'Say what was done.' } : {})}
        />
        {order.asset.status === 'out_of_service' ? (
          <Checkbox isSelected={back} onChange={setBack}>
            Put the asset back in service
          </Checkbox>
        ) : null}
        {step.isError ? <Notice tone="error">{errorText(step.error, 'Try again.')}</Notice> : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Go back
        </Button>
        <Button
          variant="primary"
          isPending={step.isPending}
          pendingLabel="Completing…"
          onPress={() => {
            setTried(true);
            if (workDone.trim() === '') return;
            step.mutate(
              () =>
                workOrderSteps.complete(
                  order.id,
                  workDone.trim(),
                  order.asset.status === 'out_of_service' && back,
                ),
              {
                onSuccess: () => {
                  toasts.show({ title: 'Work order completed' });
                  onClose();
                },
              },
            );
          }}
        >
          Complete work order
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function WorkOrderPage() {
  const { workOrderId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const allowed = session !== null && can(session, 'assets.read');
  const order = useQuery({ ...workOrderQuery(workOrderId), enabled: allowed });
  const step = useStep(workOrderId);
  const [dialog, setDialog] = useState<'complete' | 'cancel' | null>(null);
  if (!allowed) return <NotAllowed title="Work order" what="see work orders" />;
  const o = order.data;
  const maintain = can(session, 'assets.maintain');
  const working = o !== undefined && (o.status === 'open' || o.status === 'in_progress');
  return (
    <div className={styles.page}>
      <PageHeader title={o === undefined ? 'Work order' : `Work order ${o.workOrderNumber}`} />
      <p className={styles.meta}>
        <Link to="/assets">Assets</Link>
      </p>
      {online ? null : (
        <Notice tone="info">
          You&apos;re offline. Steps work again when you&apos;re back online.
        </Notice>
      )}
      {order.isPending ? <LoadingBlock label="Loading the work order" rows={6} /> : null}
      {order.isError ? (
        <LoadFailed
          error={order.error}
          fallback="The work order couldn't be loaded. Try again."
          onRetry={() => void order.refetch()}
        />
      ) : null}
      {o === undefined ? null : (
        <>
          {maintain && working ? (
            <div className={styles.actions}>
              {o.status === 'open' ? (
                <Button
                  isDisabled={!online}
                  isPending={step.isPending}
                  pendingLabel="Starting…"
                  onPress={() => {
                    step.mutate(() => workOrderSteps.start(o.id), {
                      onSuccess: () => {
                        toasts.show({ title: 'Work started' });
                      },
                    });
                  }}
                >
                  Start work
                </Button>
              ) : null}
              <Button
                variant="primary"
                isDisabled={!online}
                onPress={() => {
                  setDialog('complete');
                }}
              >
                Complete work order
              </Button>
              <Button
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  step.reset();
                  setDialog('cancel');
                }}
              >
                Cancel work order
              </Button>
            </div>
          ) : null}
          <Section title="Work order">
            <dl className={styles.facts}>
              <dt>Status</dt>
              <dd>
                <WorkOrderStatusTag status={o.status} />
                {o.statusReason === null ? null : (
                  <span className={styles.meta}> {o.statusReason}</span>
                )}
              </dd>
              <dt>Kind</dt>
              <dd>{WORK_ORDER_KIND_LABELS[o.kind]}</dd>
              <dt>Asset</dt>
              <dd>
                <Link to="/assets/$assetId" params={{ assetId: o.asset.id }}>
                  <span className="wodi-id">{o.asset.tag}</span> {o.asset.name}
                </Link>{' '}
                <AssetStatusTag status={o.asset.status} />
              </dd>
              <dt>Incident</dt>
              <dd>
                {o.incident === null ? (
                  'None linked'
                ) : (
                  <Link to="/incidents/$incidentId" params={{ incidentId: o.incident.id }}>
                    <span className="wodi-id">{o.incident.incidentNumber}</span>
                  </Link>
                )}
              </dd>
              <dt>Opened</dt>
              <dd>
                {dateTimeText(o.openedAt)}, {o.openedBy.displayName}
                {o.tookOutOfService ? ', took the asset out of service' : ''}
              </dd>
              <dt>Started</dt>
              <dd>{o.startedAt === null ? 'Not yet' : dateTimeText(o.startedAt)}</dd>
              <dt>Completed</dt>
              <dd>
                {o.completedAt === null
                  ? 'Not yet'
                  : `${dateTimeText(o.completedAt)}, ${o.completedBy?.displayName ?? ''}`}
              </dd>
            </dl>
            <h3>Problem</h3>
            <p className={styles.prose}>{o.problem}</p>
            {o.workDone === null ? null : (
              <>
                <h3>Work done</h3>
                <p className={styles.prose}>{o.workDone}</p>
              </>
            )}
          </Section>
          <Section title="Parts used" description="Taken from stock when recorded.">
            <div className={styles.form}>
              {o.parts.length === 0 ? (
                <p className={styles.meta}>No parts recorded.</p>
              ) : (
                <ul className={styles.list} aria-label="Parts used">
                  {o.parts.map((p) => (
                    <li key={p.id} className={styles.item}>
                      <div className={styles.itemMain}>
                        <span>
                          {String(p.quantity)} {p.unit}, {p.item.name}
                        </span>
                        <span className={styles.meta}>
                          {p.store.name}, batch <span className="wodi-id">{p.batchNumber}</span>,{' '}
                          {dateTimeText(p.recordedAt)} by {p.recordedBy.displayName}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {maintain && working ? <AddPart order={o} online={online} /> : null}
            </div>
          </Section>
          {dialog === 'complete' ? (
            <CompleteDialog
              order={o}
              onClose={() => {
                setDialog(null);
              }}
            />
          ) : null}
          {dialog === 'cancel' ? (
            <ReasonDialog
              title="Cancel the work order"
              isOpen
              onOpenChange={(open) => {
                if (!open) setDialog(null);
              }}
              confirmLabel="Cancel work order"
              pendingLabel="Cancelling…"
              isPending={step.isPending}
              error={step.isError ? errorText(step.error, 'That didn’t work. Try again.') : null}
              onConfirm={(reason) => {
                step.mutate(() => workOrderSteps.cancel(o.id, reason), {
                  onSuccess: () => {
                    toasts.show({ title: 'Work order cancelled' });
                    setDialog(null);
                  },
                });
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
